import { useEffect, useState, type ReactNode } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  Drawer,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import { formatLabel, toIsoUtc } from "../labels";
import type {
  CommunityAction,
  CommunityFeatureAction,
  CommunityHealthStatus,
  CommunityMembershipType,
  CommunityOwnershipType,
  CommunityStatus,
  FilePurposeType,
  PostAction,
  PostContentType,
  VisibilityStatus,
} from "../types";

const VISIBILITY: VisibilityStatus[] = [
  "visible",
  "hidden",
  "paused",
  "restricted",
];
const CREATE_STATUSES: Array<"draft" | "active"> = ["draft", "active"];
const MEMBERSHIP_TYPES: CommunityMembershipType[] = [
  "member",
  "future_governed_type",
];
const OWNERSHIP_TYPES: CommunityOwnershipType[] = [
  "founder",
  "owner",
  "manager",
];
const POST_CONTENT: PostContentType[] = [
  "text",
  "media",
  "attachment",
  "event",
  "future_governed_type",
];
const HEALTH: CommunityHealthStatus[] = [
  "healthy",
  "monitoring",
  "at_risk",
  "restricted",
  "critical",
];
const FILE_PURPOSES: FilePurposeType[] = [
  "community_asset",
  "verification_document",
  "audit_evidence",
  "policy_attachment",
  "future_governed_type",
];

export function CreateCommunityDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    market_id?: string | null;
    description?: string | null;
    category?: string | null;
    visibility_status?: VisibilityStatus;
    status?: "draft" | "active";
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [name, setName] = useState("");
  const [marketId, setMarketId] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [visibility, setVisibility] = useState<VisibilityStatus>("visible");
  const [status, setStatus] = useState<"draft" | "active">("draft");

  useEffect(() => {
    if (!open) return;
    setName("");
    setMarketId("");
    setDescription("");
    setCategory("");
    setVisibility("visible");
    setStatus("draft");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create Community"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={name.trim().length === 0}
            onClick={() =>
              void onSubmit({
                name: name.trim(),
                ...(marketId.trim() ? { market_id: marketId.trim() } : {}),
                ...(description.trim()
                  ? { description: description.trim() }
                  : {}),
                ...(category.trim() ? { category: category.trim() } : {}),
                visibility_status: visibility,
                status,
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Community Membership is independent of Ownership, Business
          Relationship, and Creator Program Membership.
        </Text>
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Input
          label="Market ID (optional UUID)"
          value={marketId}
          onChange={(event) => setMarketId(event.target.value)}
        />
        <Input
          label="Category"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        />
        <Textarea
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Select
          label="Visibility"
          value={visibility}
          onChange={(event) =>
            setVisibility(event.target.value as VisibilityStatus)
          }
          options={VISIBILITY.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Select
          label="Initial status"
          value={status}
          onChange={(event) =>
            setStatus(event.target.value as "draft" | "active")
          }
          options={CREATE_STATUSES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function EditCommunityDrawer({
  open,
  name,
  marketId,
  description,
  category,
  visibilityStatus,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  name: string;
  marketId: string;
  description: string;
  category: string;
  visibilityStatus: VisibilityStatus;
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    market_id: string | null;
    description: string | null;
    category: string | null;
    visibility_status: VisibilityStatus;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [nextName, setNextName] = useState(name);
  const [nextMarketId, setNextMarketId] = useState(marketId);
  const [nextDescription, setNextDescription] = useState(description);
  const [nextCategory, setNextCategory] = useState(category);
  const [visibility, setVisibility] = useState(visibilityStatus);

  useEffect(() => {
    if (!open) return;
    setNextName(name);
    setNextMarketId(marketId);
    setNextDescription(description);
    setNextCategory(category);
    setVisibility(visibilityStatus);
  }, [open, name, marketId, description, category, visibilityStatus]);

  if (!open) return null;

  return (
    <Drawer open={open} onClose={onClose} title="Edit Community">
      <Stack gap={16}>
        <Input
          label="Name"
          value={nextName}
          onChange={(event) => setNextName(event.target.value)}
        />
        <Input
          label="Market ID"
          value={nextMarketId}
          onChange={(event) => setNextMarketId(event.target.value)}
        />
        <Input
          label="Category"
          value={nextCategory}
          onChange={(event) => setNextCategory(event.target.value)}
        />
        <Textarea
          label="Description"
          value={nextDescription}
          onChange={(event) => setNextDescription(event.target.value)}
        />
        <Select
          label="Visibility"
          value={visibility}
          onChange={(event) =>
            setVisibility(event.target.value as VisibilityStatus)
          }
          options={VISIBILITY.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? (
          <Alert tone="danger" title="Update failed">
            {error}
          </Alert>
        ) : null}
        <Button
          variant="primary"
          loading={busy}
          disabled={nextName.trim().length === 0}
          onClick={() =>
            void onSubmit({
              name: nextName.trim(),
              market_id: nextMarketId.trim() || null,
              description: nextDescription.trim() || null,
              category: nextCategory.trim() || null,
              visibility_status: visibility,
            })
          }
        >
          Save
        </Button>
      </Stack>
    </Drawer>
  );
}

/** What each status action does for players (i139). */
const IN_THE_APP: Record<CommunityAction, string> = {
  activate: "Works as normal.",
  restrict: "No new posts and no new members or join requests. Comments, hypes and the chat stay open.",
  pause: "Hidden from Discover and search, and read-only for members: no posts, comments, hypes, votes or chat.",
  restore: "Works as normal again. A community the founder archived stays archived.",
  archive: "Archived, the same as the founder archiving it. The founder can unarchive it.",
  close: "Archived and read-only for everyone, and the founder can't unarchive it. This can't be undone from HQ.",
};

export function CommunityActionDialog({
  open,
  currentStatus,
  actions,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  currentStatus: CommunityStatus;
  actions: CommunityAction[];
  onClose: () => void;
  onSubmit: (input: {
    action: CommunityAction;
    reason: string;
    approval_id?: string;
    high_impact?: boolean;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [action, setAction] = useState<CommunityAction>(actions[0] ?? "activate");
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");
  const [highImpact, setHighImpact] = useState(false);
  const firstAction = actions[0] ?? "activate";

  useEffect(() => {
    if (!open) return;
    setAction(firstAction);
    setReason("");
    setApprovalId("");
    setHighImpact(false);
  }, [open, firstAction]);

  const needsDual = action === "restrict" || action === "close";
  const restoreFromRestricted =
    action === "restore" && currentStatus === "restricted";
  const approvalRequired = needsDual || (restoreFromRestricted && highImpact);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Community status action"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={
              action === "restrict" || action === "close" ? "danger" : "primary"
            }
            loading={busy}
            disabled={
              reason.trim().length < 3 ||
              (approvalRequired && approvalId.trim().length === 0)
            }
            onClick={() =>
              void onSubmit({
                action,
                reason: reason.trim(),
                ...(approvalId.trim()
                  ? { approval_id: approvalId.trim() }
                  : {}),
                ...(restoreFromRestricted ? { high_impact: highImpact } : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Restrict and close require Dual Approval. Restore from restricted may
          require Dual Approval when marked high-impact. Close and archive keep
          memberships, ownership, posts, and history.
        </Text>
        <Select
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value as CommunityAction)}
          options={actions.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Text role="small" as="p">
          In the app: {IN_THE_APP[action]}
        </Text>
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {restoreFromRestricted ? (
          <Checkbox
            label="High-impact restore (Dual Approval)"
            checked={highImpact}
            onChange={(event) => setHighImpact(event.target.checked)}
          />
        ) : null}
        <ApprovalPickerField
          label={
            approvalRequired
              ? "Dual approved Approval (required)"
              : "Approved Approval (when required)"
          }
          value={approvalId}
          onChange={setApprovalId}
          required={approvalRequired}
          minAuthority={approvalRequired ? "dual" : undefined}
        />
        {error ? (
          <Alert tone="danger" title="Action failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function ReasonActionDialog({
  open,
  title,
  notice,
  extraFields,
  requireReason = true,
  confirmLabel = "Confirm",
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  title: string;
  notice?: string;
  extraFields?: ReactNode;
  requireReason?: boolean;
  confirmLabel?: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={requireReason && reason.trim().length < 3}
            onClick={() => void onSubmit(reason.trim())}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        {notice ? (
          <Text role="small" color="secondary" as="p">
            {notice}
          </Text>
        ) : null}
        {extraFields}
        <Textarea
          label={requireReason ? "Reason" : "Reason (optional unless required)"}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Action failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateMembershipDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    user_id: string;
    mode: "invite" | "request";
    membership_type: CommunityMembershipType;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [userId, setUserId] = useState("");
  const [mode, setMode] = useState<"invite" | "request">("invite");
  const [membershipType, setMembershipType] =
    useState<CommunityMembershipType>("member");

  useEffect(() => {
    if (!open) return;
    setUserId("");
    setMode("invite");
    setMembershipType("member");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add Community Membership"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={userId.trim().length === 0}
            onClick={() =>
              void onSubmit({
                user_id: userId.trim(),
                mode,
                membership_type: membershipType,
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Invite creates invited; request creates requested. This is not
          Ownership, a Business Relationship, or Creator Program Membership.
        </Text>
        <Input
          label="User ID"
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
        />
        <Select
          label="Mode"
          value={mode}
          onChange={(event) =>
            setMode(event.target.value as "invite" | "request")
          }
          options={[
            { value: "invite", label: "Invite" },
            { value: "request", label: "Request" },
          ]}
        />
        <Select
          label="Membership type"
          value={membershipType}
          onChange={(event) =>
            setMembershipType(event.target.value as CommunityMembershipType)
          }
          options={MEMBERSHIP_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateOwnershipDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    user_id: string;
    ownership_type: CommunityOwnershipType;
    status: "proposed" | "active";
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [userId, setUserId] = useState("");
  const [ownershipType, setOwnershipType] =
    useState<CommunityOwnershipType>("owner");
  const [status, setStatus] = useState<"proposed" | "active">("proposed");

  useEffect(() => {
    if (!open) return;
    setUserId("");
    setOwnershipType("owner");
    setStatus("proposed");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Propose Community Ownership"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={userId.trim().length === 0}
            onClick={() =>
              void onSubmit({
                user_id: userId.trim(),
                ownership_type: ownershipType,
                status,
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Founder, owner, and manager are distinct ownership types. Transfer is
          a later action with Manager Approval.
        </Text>
        <Input
          label="User ID"
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
        />
        <Select
          label="Ownership type"
          value={ownershipType}
          onChange={(event) =>
            setOwnershipType(event.target.value as CommunityOwnershipType)
          }
          options={OWNERSHIP_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Select
          label="Initial status"
          value={status}
          onChange={(event) =>
            setStatus(event.target.value as "proposed" | "active")
          }
          options={[
            { value: "proposed", label: "proposed" },
            { value: "active", label: "active" },
          ]}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function TransferOwnershipDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    new_user_id: string;
    reason: string;
    approval_id: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [newUserId, setNewUserId] = useState("");
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");

  useEffect(() => {
    if (!open) return;
    setNewUserId("");
    setReason("");
    setApprovalId("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Transfer Community Ownership"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={busy}
            disabled={
              newUserId.trim().length === 0 ||
              reason.trim().length < 3 ||
              approvalId.trim().length === 0
            }
            onClick={() =>
              void onSubmit({
                new_user_id: newUserId.trim(),
                reason: reason.trim(),
                approval_id: approvalId.trim(),
              })
            }
          >
            Transfer
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Transfer requires Manager Approval and a new user ID. History stays
          immutable.
        </Text>
        <Input
          label="New User ID"
          value={newUserId}
          onChange={(event) => setNewUserId(event.target.value)}
        />
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <ApprovalPickerField
          label="Manager approved Approval"
          value={approvalId}
          onChange={setApprovalId}
          minAuthority="manager"
        />
        {error ? (
          <Alert tone="danger" title="Transfer failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreatePostDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    content_type: PostContentType;
    content_reference: string;
    author_user_id?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [contentType, setContentType] = useState<PostContentType>("text");
  const [contentReference, setContentReference] = useState("");
  const [authorUserId, setAuthorUserId] = useState("");

  useEffect(() => {
    if (!open) return;
    setContentType("text");
    setContentReference("");
    setAuthorUserId("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create Community Post"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={contentReference.trim().length === 0}
            onClick={() =>
              void onSubmit({
                content_type: contentType,
                content_reference: contentReference.trim(),
                ...(authorUserId.trim()
                  ? { author_user_id: authorUserId.trim() }
                  : {}),
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Posts start as draft. Publish is a separate action. This is HQ
          governance of community posts, not the Play social feed.
        </Text>
        <Select
          label="Content type"
          value={contentType}
          onChange={(event) =>
            setContentType(event.target.value as PostContentType)
          }
          options={POST_CONTENT.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Content reference"
          value={contentReference}
          onChange={(event) => setContentReference(event.target.value)}
        />
        <Input
          label="Author User ID (optional)"
          value={authorUserId}
          onChange={(event) => setAuthorUserId(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function PostActionDialog({
  open,
  action,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  action: PostAction | null;
  onClose: () => void;
  onSubmit: (input: {
    reason: string;
    content_reference?: string;
    content_type?: PostContentType;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [reason, setReason] = useState("");
  const [contentReference, setContentReference] = useState("");
  const [contentType, setContentType] = useState<PostContentType>("text");

  useEffect(() => {
    if (!open) return;
    setReason("");
    setContentReference("");
    setContentType("text");
  }, [open]);

  const isEdit = action === "edit";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={action ? `Post ${formatLabel(action)}` : "Post action"}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={
              action === "remove" || action === "restrict" ? "danger" : "primary"
            }
            loading={busy}
            disabled={
              reason.trim().length < 3 ||
              (isEdit && contentReference.trim().length === 0)
            }
            onClick={() =>
              void onSubmit({
                reason: reason.trim(),
                ...(isEdit
                  ? {
                      content_reference: contentReference.trim(),
                      content_type: contentType,
                    }
                  : {}),
              })
            }
          >
            Confirm
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        {isEdit ? (
          <>
            <Select
              label="Content type"
              value={contentType}
              onChange={(event) =>
                setContentType(event.target.value as PostContentType)
              }
              options={POST_CONTENT.map((item) => ({
                value: item,
                label: formatLabel(item),
              }))}
            />
            <Input
              label="Content reference"
              value={contentReference}
              onChange={(event) => setContentReference(event.target.value)}
            />
          </>
        ) : null}
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Action failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateHealthSnapshotDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    status: CommunityHealthStatus;
    measured_at?: string;
    metrics_snapshot?: Record<string, unknown>;
    risk_summary?: string | null;
    reviewed_by_user_id?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [status, setStatus] = useState<CommunityHealthStatus>("monitoring");
  const [measuredAt, setMeasuredAt] = useState("");
  const [riskSummary, setRiskSummary] = useState("");
  const [reviewedBy, setReviewedBy] = useState("");
  const [metrics, setMetrics] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStatus("monitoring");
    setMeasuredAt("");
    setRiskSummary("");
    setReviewedBy("");
    setMetrics("");
    setLocalError(null);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Record health snapshot"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() => {
              let snapshot: Record<string, unknown> | undefined;
              if (metrics.trim()) {
                try {
                  const parsed: unknown = JSON.parse(metrics);
                  if (
                    typeof parsed !== "object" ||
                    parsed === null ||
                    Array.isArray(parsed)
                  ) {
                    setLocalError("Metrics snapshot must be a JSON object.");
                    return;
                  }
                  snapshot = parsed as Record<string, unknown>;
                } catch {
                  setLocalError("Metrics snapshot must be valid JSON.");
                  return;
                }
              }
              setLocalError(null);
              void onSubmit({
                status,
                ...(measuredAt.trim()
                  ? { measured_at: toIsoUtc(measuredAt) }
                  : {}),
                ...(snapshot ? { metrics_snapshot: snapshot } : {}),
                ...(riskSummary.trim()
                  ? { risk_summary: riskSummary.trim() }
                  : {}),
                ...(reviewedBy.trim()
                  ? { reviewed_by_user_id: reviewedBy.trim() }
                  : {}),
              });
            }}
          >
            Record
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Snapshots are append-only. Community health_status is a summary of
          the latest snapshot, not a substitute for history.
        </Text>
        <Select
          label="Health status"
          value={status}
          onChange={(event) =>
            setStatus(event.target.value as CommunityHealthStatus)
          }
          options={HEALTH.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Measured at (optional)"
          type="datetime-local"
          value={measuredAt}
          onChange={(event) => setMeasuredAt(event.target.value)}
        />
        <Textarea
          label="Risk summary"
          value={riskSummary}
          onChange={(event) => setRiskSummary(event.target.value)}
        />
        <Input
          label="Reviewed by User ID (optional)"
          value={reviewedBy}
          onChange={(event) => setReviewedBy(event.target.value)}
        />
        <Textarea
          label="Metrics snapshot JSON (optional object)"
          value={metrics}
          onChange={(event) => setMetrics(event.target.value)}
        />
        {localError || error ? (
          <Alert tone="danger" title="Create failed">
            {localError ?? error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateFeatureDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    creator_campaign_id?: string | null;
    starts_at?: string | null;
    ends_at?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [campaignId, setCampaignId] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");

  useEffect(() => {
    if (!open) return;
    setCampaignId("");
    setStartsAt("");
    setEndsAt("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Propose feature placement"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() =>
              void onSubmit({
                ...(campaignId.trim()
                  ? { creator_campaign_id: campaignId.trim() }
                  : {}),
                ...(startsAt.trim() ? { starts_at: toIsoUtc(startsAt) } : {}),
                ...(endsAt.trim() ? { ends_at: toIsoUtc(endsAt) } : {}),
              })
            }
          >
            Propose
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Feature placement starts proposed. Approve requires Manager Approval
          and is Marketing-aligned. Marketing never sees private moderation
          evidence.
        </Text>
        <Input
          label="Creator Campaign ID (optional)"
          value={campaignId}
          onChange={(event) => setCampaignId(event.target.value)}
        />
        <Input
          label="Starts at (optional)"
          type="datetime-local"
          value={startsAt}
          onChange={(event) => setStartsAt(event.target.value)}
        />
        <Input
          label="Ends at (optional)"
          type="datetime-local"
          value={endsAt}
          onChange={(event) => setEndsAt(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function FeatureActionDialog({
  open,
  action,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  action: CommunityFeatureAction | null;
  onClose: () => void;
  onSubmit: (input: {
    reason: string;
    approval_id?: string;
    starts_at?: string | null;
    ends_at?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason("");
    setApprovalId("");
    setStartsAt("");
    setEndsAt("");
  }, [open]);

  const isApprove = action === "approve";
  const isSchedule = action === "schedule";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={action ? `Feature ${formatLabel(action)}` : "Feature action"}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={action === "remove" ? "danger" : "primary"}
            loading={busy}
            disabled={
              reason.trim().length < 3 ||
              (isApprove && approvalId.trim().length === 0)
            }
            onClick={() =>
              void onSubmit({
                reason: reason.trim(),
                ...(approvalId.trim()
                  ? { approval_id: approvalId.trim() }
                  : {}),
                ...(isSchedule && startsAt.trim()
                  ? { starts_at: toIsoUtc(startsAt) }
                  : {}),
                ...(isSchedule && endsAt.trim()
                  ? { ends_at: toIsoUtc(endsAt) }
                  : {}),
              })
            }
          >
            Confirm
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        {isApprove ? (
          <Text role="small" color="secondary" as="p">
            Manager Approval is required. This is not a commercial payment
            workflow.
          </Text>
        ) : null}
        {isSchedule ? (
          <>
            <Input
              label="Starts at"
              type="datetime-local"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
            />
            <Input
              label="Ends at"
              type="datetime-local"
              value={endsAt}
              onChange={(event) => setEndsAt(event.target.value)}
            />
          </>
        ) : null}
        {isApprove ? (
          <ApprovalPickerField
            label="Manager approved Approval"
            value={approvalId}
            onChange={setApprovalId}
            minAuthority="manager"
          />
        ) : null}
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Action failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function AttachFileDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    file_asset_id: string;
    purpose_type: FilePurposeType;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [fileAssetId, setFileAssetId] = useState("");
  const [purposeType, setPurposeType] =
    useState<FilePurposeType>("community_asset");

  useEffect(() => {
    if (!open) return;
    setFileAssetId("");
    setPurposeType("community_asset");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Attach File Asset"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={fileAssetId.trim().length === 0}
            onClick={() =>
              void onSubmit({
                file_asset_id: fileAssetId.trim(),
                purpose_type: purposeType,
              })
            }
          >
            Attach
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Documents are workflow records. File Assets are stored files linked
          through File Association.
        </Text>
        <Input
          label="File Asset ID"
          value={fileAssetId}
          onChange={(event) => setFileAssetId(event.target.value)}
        />
        <Select
          label="Purpose"
          value={purposeType}
          onChange={(event) =>
            setPurposeType(event.target.value as FilePurposeType)
          }
          options={FILE_PURPOSES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? (
          <Alert tone="danger" title="Attach failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}
